import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button } from './Button';
import { cn } from './utils';
import { Calendar } from './Calendar';
import { es } from 'date-fns/locale';

export function DateRangePicker({
	disabled,
	className,
	onRangeChange,
	floatingLabel = '',
	defaultValue,
	popoverContentClassName
}: {
	disabled?: boolean;
	className?: string;
	onRangeChange: (selected: DateRange | undefined) => any;
	floatingLabel?: string;
	defaultValue?: DateRange;
	popoverContentClassName?: string;
}) {
	const [date, setDate] = React.useState<DateRange | undefined>(defaultValue);
	const [open, setOpen] = React.useState(false);

	return (
		<div
			data-disabled={disabled || false}
			className={cn('relative grid gap-2 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-60', className)}
		>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						id="date"
						variant={'default'}
						className={cn(
							'shrink-0 justify-start text-left light:bg-neutral-900/70',
							!date && 'text-neutral-100',
							floatingLabel &&
								'h-12 border border-neutral-800 bg-neutral-925 transition-all has-[:focus]:border-primary-500 has-[:focus]:outline-none has-[:focus]:ring-2 has-[:focus]:ring-primary-500 light:bg-neutral-950',
							className
						)}
					>
						<CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
						{date?.from && date?.to ? (
							<>
								{format(date.from, 'LLL dd, y', { locale: es })} - {format(date.to, 'LLL dd, y', { locale: es })}
								<X
									onClick={(e) => {
										e.stopPropagation();
										setDate(undefined);
										onRangeChange(undefined);
									}}
									className="ml-2 size-4 shrink-0"
								/>
							</>
						) : (
							<span>{floatingLabel ? 'Seleccionar período' : 'Rango de fechas'}</span>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className={cn("w-auto p-0", popoverContentClassName)} align="start">
					<Calendar
						initialFocus
						mode="range"
						defaultMonth={date?.from}
						selected={date}
						onSelect={(selected) => {
							setDate(selected);
							// Only close popover and notify parent when both dates are selected
							if (selected?.from && selected?.to) {
								setOpen(false);
								onRangeChange(selected);
							}
						}}
						locale={es}
						numberOfMonths={2}
					/>
				</PopoverContent>
			</Popover>
			{floatingLabel && (
				<label className="absolute -top-2 left-2 max-w-[90%] overflow-hidden overflow-ellipsis text-nowrap rounded bg-neutral-1000 px-1 text-xs font-medium text-primary-200/80 light:bg-primary-300 light:text-primary-900">
					{floatingLabel}
				</label>
			)}
		</div>
	);
}
